import React, { useCallback, useMemo, useState, forwardRef } from 'react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { IconButton, LinkIconButton } from '@atlaskit/button/new';
import DownloadIcon from '@atlaskit/icon/core/migration/download';
import TrashIcon from '@atlaskit/icon/core/migration/delete--trash';
import { useFileState, useMediaClient } from '@atlaskit/media-client-react';
import { AbuseModal } from '@atlaskit/media-ui/abuseModal';
import { useIntl } from '@atlassian/jira-intl';
import { useCanDeleteAttachment } from '@atlassian/jira-issue-attachments-base/src/controllers/index.tsx';
import { listView } from '@atlassian/jira-issue-attachments-base/src/ui/types.tsx';
import { issueAttachmentsDeleteModalEntrypoint } from '@atlassian/jira-issue-attachments-base/src/ui/with-issue-attachments-delete/entrypoint.tsx';
import { isAttachmentNumberApproachLimit } from '@atlassian/jira-issue-attachments-limit/src/common/utils.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { AttachmentActionItem } from '../../types.tsx';
import { messages } from './messages.tsx';

interface ActionsProps {
	item: AttachmentActionItem;
	totalCount: number;
}

const DeleteButton = forwardRef<
	HTMLButtonElement,
	{
		buttonProps: React.ComponentProps<typeof IconButton>;
		onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
	}
>(({ buttonProps, onClick }, ref) => (
	<Inline xcss={buttonStyle}>
		<IconButton {...buttonProps} ref={ref} onClick={onClick} />
	</Inline>
));

const MediaCardActions = ({ item, totalCount }: ActionsProps) => {
	const { formatMessage } = useIntl();
	const { fileState } = useFileState(item.identifier.id);
	const [isAbuseModalOpen, setIsAbuseModalOpen] = useState(false);
	const mediaClient = useMediaClient();
	const canDeleteAttachmentMap = useCanDeleteAttachment();

	const isAttachmentLimitReached = isAttachmentNumberApproachLimit(totalCount);
	const canDelete = canDeleteAttachmentMap[item.identifier.attachmentId];

	const downloadFn = useCallback(
		async (event?: React.MouseEvent<HTMLButtonElement>) => {
			event?.stopPropagation();
			event?.preventDefault();
			try {
				await mediaClient.file.downloadBinary(item.identifier.id);
			} catch (error) {
				if (error instanceof Error) {
					fireErrorAnalytics({
						meta: {
							id: 'jira.issue.attachments.downloadBinary',
							packageName: 'jira.issue.attachments',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			}
			setIsAbuseModalOpen(false);
		},
		[item.identifier.id, mediaClient.file],
	);

	const handleDownload = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			event.preventDefault();
			if (fileState?.status !== 'error' && fileState?.abuseClassification) {
				setIsAbuseModalOpen(true);
			} else {
				downloadFn();
			}
		},
		[downloadFn, fileState],
	);

	const handleCloseAbuseModal = useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
		event?.stopPropagation();
		event?.preventDefault();
		setIsAbuseModalOpen(false);
	}, []);

	const deleteEntryPointProps = useMemo(
		() => ({
			attachmentView: listView,
			deletingAttachmentId: item.identifier.attachmentId,
		}),
		[item.identifier.attachmentId],
	);

	const deleteModalProps = useMemo(() => ({ width: 'small' }), []);

	const downloadButtonCommonProps = {
		testId: `issue.views.issue-base.content.attachment.grid-view.download.${item.identifier.attachmentId}`,
		appearance: 'subtle' as const,
		icon: DownloadIcon,
		label: formatMessage(messages.download),
	} as const;

	const deleteButtonCommonProps = {
		testId: `issue.views.issue-base.content.attachment.grid-view.delete.${item.identifier.attachmentId}`,
		appearance: 'subtle' as const,
		icon: TrashIcon,
		label: formatMessage(messages.delete),
	} as const;

	return (
		<>
			{isAbuseModalOpen && (
				<AbuseModal
					isOpen={isAbuseModalOpen}
					onConfirm={downloadFn}
					onClose={handleCloseAbuseModal}
				/>
			)}
			<Box
				xcss={actionsStyle}
				testId="issue-view-base.content.attachment.grid-view.ui.components.media-card.media-card-actions"
			>
				<Inline space="space.050">
					{isAttachmentLimitReached ? (
						<Inline xcss={buttonStyle}>
							<LinkIconButton
								{...downloadButtonCommonProps}
								href={`/rest/api/3/attachment/content/${item.identifier.attachmentId}`}
								onClick={(event) => event.stopPropagation()}
							/>
						</Inline>
					) : (
						<Inline xcss={buttonStyle}>
							<IconButton {...downloadButtonCommonProps} onClick={handleDownload} />
						</Inline>
					)}
					{canDelete && (
						<ModalEntryPointPressableTrigger
							entryPoint={issueAttachmentsDeleteModalEntrypoint}
							entryPointProps={deleteEntryPointProps}
							modalProps={deleteModalProps}
							useInternalModal
							interactionName="issue-attachment-grid-view-delete-attachment"
						>
							{({ ref }) => (
								<DeleteButton
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									ref={ref as React.Ref<HTMLButtonElement>}
									buttonProps={deleteButtonCommonProps}
									onClick={(event: React.MouseEvent<HTMLButtonElement>) => event.stopPropagation()}
								/>
							)}
						</ModalEntryPointPressableTrigger>
					)}
				</Inline>
			</Box>
		</>
	);
};

const actionsStyle = xcss({
	position: 'absolute',
	top: token('space.050'),
	right: token('space.050'),
	zIndex: '1',
	transition: 'opacity 0.2s ease-in-out',
});

const buttonStyle = xcss({
	transition: 'background-color 0.2s',
	borderRadius: token('border.radius.100'),
	backgroundColor: 'elevation.surface.overlay',

	':hover': {
		backgroundColor: 'elevation.surface.overlay.hovered',
	},

	':focus-within': {
		backgroundColor: 'elevation.surface.overlay.hovered',
		outlineWidth: token('border.width.outline'),
		outlineStyle: 'solid',
		outlineOffset: 'space.025',
		outlineColor: 'color.border.focused',
	},
});

export default MediaCardActions;
